import { Reference } from '@ninetailed/experience.js';
import {
  ExperienceConfiguration,
  useExperience,
  useNinetailed,
} from '@ninetailed/experience.js-next';
import { useExperienceTrackingContext } from 'contexts/experience-tracking';
import useIsClient from 'hooks/common/use-is-client';
import { useCallback, useEffect } from 'react';

type NavigationExperimentInput = {
  baseline: Reference;
  experiences: ExperienceConfiguration<Reference>[];
  experimentName?: string; // Optional name for debugging
};

export type NavigationExperimentOutput = {
  experience: any;
  variant: any;
  variantIndex: number;
  loading: boolean;
  status: 'loading' | 'success' | 'error';
  trackExperimentView: () => void;
};

/**
 * Navigation-specific experiment wrapper with tracking functionality
 * Similar to useExperiment but optimized for navigation use cases
 */
export const useNavigationExperiment = ({
  baseline,
  experiences,
  experimentName,
}: NavigationExperimentInput): NavigationExperimentOutput => {
  const { isClient } = useIsClient();
  const { experience, variant, variantIndex, loading, status } = useExperience({
    baseline,
    experiences,
  });

  const { trackedExperiences, addTrackedExperience } =
    useExperienceTrackingContext();
  const { trackComponentView } = useNinetailed();

  const trackExperimentView = useCallback(() => {
    if (
      !isClient ||
      !experience ||
      loading ||
      status !== 'success' ||
      // Skip personalization tracking
      experience?.type === 'nt_personalization'
    ) {
      return;
    }

    const trackingKey = `${experience.id}.${variantIndex}`;

    // Only track if not already tracked
    if (!trackedExperiences.has(trackingKey)) {
      // Create fake DOM element for navigation tracking
      const fakeElement = document.createElement('div');
      fakeElement.setAttribute(
        'data-navigation-experiment',
        experimentName || 'navigation'
      );

      const payload = {
        element: fakeElement,
        experience,
        variant,
        variantIndex,
      };

      trackComponentView(payload);
      addTrackedExperience(trackingKey);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Navigation experiment tracked: ${trackingKey}`, {
          experimentName,
          experience: experience.name,
          variantIndex,
        });
      }
    }
  }, [
    isClient,
    experience,
    loading,
    status,
    variantIndex,
    trackedExperiences,
    trackComponentView,
    addTrackedExperience,
    experimentName,
    variant,
  ]);

  // Auto-track navigation experiments when they resolve
  // Navigation is typically always visible, so we track immediately
  useEffect(() => {
    if (experience && !loading && status === 'success') {
      trackExperimentView();
    }
  }, [experience, loading, status, trackExperimentView]);

  return {
    experience,
    variant,
    variantIndex,
    loading,
    status,
    trackExperimentView,
  };
};
